import {Component, Inject, OnInit, ViewChild, ViewEncapsulation} from '@angular/core';
import {FormControl, UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {FormOptionsLists} from '../../../../shared/FormOptionsLists';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import {MatSelectChange} from '@angular/material/select';
import {Word} from '../../../../dtos/vocab/entry/word';
import {MatStepper} from '@angular/material/stepper';
import {Level} from '../../../../shared/Level';
import {Type} from '../../../../shared/Type';
import {Article} from '../../../../shared/Article';
import {Category} from '../../../../dtos/vocab/category/category';
import {WordService} from '../../../../services/vocab/word.service';
import {Country} from '../../../../shared/Country';
import {Observable} from 'rxjs';
import {map, startWith} from 'rxjs/operators';
import {NotificationService} from '../../../../services/common/notification.service';
import {VocabEntryCreationDto} from '../../../../dtos/vocab/entry/vocab-entry-creation-dto';
import {GuideWord} from '../../../../dtos/guide-word';
import {SharedService} from "../../../../services/common/shared.service";
import {SelectDialogComponent} from "../../../select/select-dialog/select-dialog.component";
import {References} from "../../../../dtos/references";

export interface DialogData {
  id: number;
}

@Component({
    selector: 'app-new-word',
    templateUrl: './new-word.component.html',
    styleUrls: ['./new-word.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})

export class NewWordComponent implements OnInit {
  //TODO check if selecting and loading guidewords for existing words work

  @ViewChild('stepper') stepper: MatStepper;

  formOptionsLists = new FormOptionsLists();
  levelList = this.formOptionsLists.levelList.filter(o => o !== 'NONE');
  typeList = this.formOptionsLists.typeList;
  countryList = this.formOptionsLists.countryList;
  wordfieldList = this.formOptionsLists.wordFieldList;
  articleList = this.formOptionsLists.articleList;
  testList = this.formOptionsLists.testList;

  filteredTests: Observable<string[]>;

  firstFormGroup: UntypedFormGroup;
  secondFormGroup: UntypedFormGroup;
  thirdFormGroup: UntypedFormGroup;
  fourthFormGroup: UntypedFormGroup;
  disableSelect = new FormControl(true);

  guideWordList: GuideWord[] = [];
  selectedCategory: Category[] = [];
  id: number;
  retrievedWord: Word;
  references: References;
  // tracks the variantsCtrl selection so onVariantsChange can tell what was just (de)selected;
  // 'DACH' already means all three countries, so it's kept mutually exclusive with the rest
  previousVariants: string[] = [];

  constructor(private notificationService: NotificationService, private wordService: WordService,
              private wordSelectDialog: MatDialog, private dialogRef: MatDialogRef<NewWordComponent>,
              @Inject(MAT_DIALOG_DATA) public injectedData: DialogData, private _formBuilder: UntypedFormBuilder,
              private sharedService: SharedService) {
    if (injectedData !== null) {
      this.id = injectedData.id;
    }
  }

  ngOnInit(): void {
    this.firstFormGroup = this._formBuilder.group({
      wordCtrl: ['', Validators.required],
      receptiveCtrl: [''],
      productiveCtrl: [''],
      variantsCtrl: [''],
      typeCtrl: [''],
      guideWordCtrl: [''],
      examCtrl: new UntypedFormControl(''),
      categoryCtrl: new UntypedFormControl(''),
    });
    this.secondFormGroup = this._formBuilder.group({
      articlesCtrl: new UntypedFormControl({value: '', disabled: true}),
      numeralCtrl: new UntypedFormControl({value: '', disabled: true}),
      pluralCtrl: new UntypedFormControl({value: '', disabled: true})
    });
    this.thirdFormGroup = this._formBuilder.group({
      presenceCtrl: new UntypedFormControl({value: '', disabled: true}),
      perfectCtrl: new UntypedFormControl({value: '', disabled: true}),
      pastCtrl: new UntypedFormControl({value: '', disabled: true})
    });
    this.fourthFormGroup = this._formBuilder.group({
      commentCtrl: new UntypedFormControl(''),
      examples: this._formBuilder.array([])
    });
    if (this.id !== undefined) { //determine between edit and create
      this.wordService.getEntry(this.id).subscribe(w => {
        this.retrievedWord = w;
        this.references = this.sharedService.extractReferences(w)['Verweise'];
        this.setForm(w);
      }, error => {
        this.notificationService.openSnackBar(error);
      });
    } else {
      // for autocomplete for "Tests"- Form
      this.filteredTests = this.firstFormGroup.controls.examCtrl?.valueChanges
        .pipe(
          startWith(''),
          map(value => this._filter(value))
        );
      this.addExample();
      this.firstFormGroup.get('guideWordCtrl')?.disable();
      this.firstFormGroup.get('categoryCtrl')?.disable();
    }
  }

  setForm(w: Word) {
    this.guideWordList = w.relatedVocab;
    this.selectedCategory = [w.category];
    this.previousVariants = this.transformCountryToMultiple(w.country);
    this.firstFormGroup = this._formBuilder.group({
      wordCtrl: new UntypedFormControl(w.name, [Validators.required]),
      receptiveCtrl: new UntypedFormControl(Level[w.receptive]),
      productiveCtrl: new UntypedFormControl(Level[w.productive]),
      variantsCtrl: new UntypedFormControl(this.previousVariants),
      typeCtrl: new UntypedFormControl(this.formOptionsLists.typeToString.get(w.vocabType)),
      guideWordCtrl: new UntypedFormControl(''),
      categoryCtrl: new UntypedFormControl([w.category]),
      examCtrl: new UntypedFormControl(w.test),
    });

    this.secondFormGroup = this._formBuilder.group({
      articlesCtrl: new UntypedFormControl({value: this.reverseMapArticle(w), disabled: true}),
      numeralCtrl: new UntypedFormControl({
        value: (w.isSingular === null) ? '' : (w.isSingular) ? 'singular' : 'plural',
        disabled: true
      }),
      pluralCtrl: new UntypedFormControl({value: w.plural, disabled: true}),
    });


    this.thirdFormGroup = this._formBuilder.group({
      presenceCtrl: new UntypedFormControl({value: w.present, disabled: true}),
      perfectCtrl: new UntypedFormControl({value: w.simplePast, disabled: true}),
      pastCtrl: new UntypedFormControl({value: w.pastParticiple, disabled: true})
    });

    this.fourthFormGroup = this._formBuilder.group({
      commentCtrl: new UntypedFormControl(w.comment),
      examples: this._formBuilder.array([])
    });

    if (this.guideWordList != null && this.guideWordList.length > 0) {
      this.sharedService.toggleAllSelection(this.firstFormGroup.controls.guideWordCtrl, this.guideWordList);
    }
    if (this.selectedCategory != null) {
      this.sharedService.toggleAllSelection(this.firstFormGroup.controls.categoryCtrl, this.selectedCategory);
    }

    w.examples.forEach(() => {
      this.addExample();
    });


    for (let i = 0; i < this.examples().length; i++) {
      this.examples().at(i).setValue({example: w.examples[i]});
    }

    this.changeWordType(this.firstFormGroup.controls.typeCtrl);
    // for autocomplete for "Tests"- Form
    this.filteredTests = this.firstFormGroup.controls.examCtrl?.valueChanges
      .pipe(
        startWith(''),
        map(value => this._filter(value))
      );
    this.firstFormGroup.get('guideWordCtrl')?.disable();
    this.firstFormGroup.get('categoryCtrl')?.disable();
  }

  /**
   * keeps 'DACH' mutually exclusive with the individual country options, since 'DACH' already
   * means all three - picking it clears the others, and picking another one while 'DACH' is
   * selected drops 'DACH' again
   */
  onVariantsChange(event: MatSelectChange): void {
    const current: string[] = event.value ?? [];
    const added = current.filter(v => !this.previousVariants.includes(v));
    let next = current;
    if (added.includes('DACH')) {
      next = ['DACH'];
    } else if (current.includes('DACH') && current.length > 1) {
      next = current.filter(v => v !== 'DACH');
    }
    this.previousVariants = next;
    this.firstFormGroup.controls.variantsCtrl.setValue(next);
  }

  transformCountryToMultiple(country: Country): string[] {
    if (country == null) {
      return [];
    }
    if (country === Country.AUSTRIA) {
      return ['Österreich'];
    }
    if (country === Country.GERMANY) {
      return ['Deutschland'];
    }
    if (country === Country.SWITZERLAND) {
      return ['Schweiz'];
    }
    if (country === Country.DCH) {
      return ['Deutschland', 'Schweiz'];
    }
    if (country === Country.AD) {
      return ['Österreich', 'Deutschland'];
    }
    if (country === Country.ACH) {
      return ['Österreich', 'Schweiz'];
    }
    if (country === Country.DACH) {
      return ['DACH'];
    }
  }

  reverseMapArticle(w: Word) {
    const params = [];
    switch (w.article) {
      case Article.der:
        params.push('der');
        break;
      case Article.das:
        params.push('das');
        break;
      case Article.die:
        params.push('die');
        break;
      case Article.derdie:
        params.push('der');
        params.push('die');
        break;
      case Article.derdas:
        params.push('der');
        params.push('das');
        break;
      case Article.diedas:
        params.push('die');
        params.push('das');
        break;
    }
    return params;
  }


  returnArticle() {
    // TODO implement 3rd Article
    const article = this.secondFormGroup.controls.articlesCtrl?.value;
    let articleParam;
    if (article === null || article?.length < 1) {
      articleParam = null;
    }
    if (article?.length === 1) {
      articleParam = Article[article[0]];
    }
    if (article?.length === 2) {
      if (article.includes('der') && article.includes('die')) {
        articleParam = Article.derdie;
      }
      if (article.includes('der') && article.includes('das')) {
        articleParam = Article.derdas;
      }
      if (article.includes('die') && article.includes('das')) {
        articleParam = Article.diedas;
      }
    }
    return articleParam;
  }

  returnExamples() {
    const examples = [];
    const q = this.examples().length;
    for (let i = 0; i < q; i++) {
      if (this.examples().at(i).value.example !== '') {
        examples.push(this.examples().at(i).value.example);
      }
    }
    return examples;
  }

  cutGuideWords() {
    const simpleGuideWords = [];
    if (this.guideWordList !== null) {
      this.guideWordList.forEach(g => {
        simpleGuideWords.push(g.id);
      });
    }
    return simpleGuideWords;
  }


  createWord() {
    if (this.firstFormGroup.controls.typeCtrl?.value) {
      const val = this.firstFormGroup.controls.typeCtrl?.value;
      if (val !== 'Nomen') {
        this.secondFormGroup.controls.articlesCtrl.setValue('');
        this.secondFormGroup.controls.numeralCtrl.setValue('');
        this.secondFormGroup.controls.pluralCtrl.setValue('');
      }
      if (val !== 'Verb') {
        this.thirdFormGroup.controls.presenceCtrl.setValue('');
        this.thirdFormGroup.controls.perfectCtrl.setValue('');
        this.thirdFormGroup.controls.pastCtrl.setValue('');
      }
    }
    const word = new VocabEntryCreationDto(
      this.id,
      this.firstFormGroup.controls.wordCtrl?.value,
      Type[this.formOptionsLists.stringToType.get(this.firstFormGroup.controls.typeCtrl?.value)],
      Level[this.firstFormGroup.controls.receptiveCtrl?.value],
      Level[this.firstFormGroup.controls.productiveCtrl?.value],
      this.returnArticle(),
      this.selectedCategory[0]?.id,
      this.secondFormGroup.controls.pluralCtrl?.value,
      this.thirdFormGroup.controls.presenceCtrl?.value,
      this.thirdFormGroup.controls.perfectCtrl?.value,
      this.thirdFormGroup.controls.pastCtrl?.value,
      Country[this.formOptionsLists.stringToCountry.get(this.transformCountry(this.firstFormGroup.controls.variantsCtrl?.value))],
      this.fourthFormGroup.controls.commentCtrl?.value,
      this.returnExamples(),
      this.firstFormGroup.controls.examCtrl?.value,
      this.isSingular(),
      this.sharedService.getIds(this.references?.vocabCategories),
      this.sharedService.getIds(this.references?.vocabEntries),
      this.sharedService.getIds(this.references?.functionalGrammarCategories),
      this.sharedService.getIds(this.references?.functionalGrammarEntries),
      this.sharedService.getIds(this.references?.systemicGrammarCategories),
      this.sharedService.getIds(this.references?.systemicGrammarEntries),
      this.sharedService.getIds(this.references?.textPatterns),
      this.sharedService.getIds(this.references?.textTypes),
      this.sharedService.getIds(this.references?.detailedCanDoStatementCategories),
      this.sharedService.getIds(this.references?.detailedCanDoStatementEntries),
      this.sharedService.getIds(this.references?.globalCanDoStatementCategories),
      this.sharedService.getIds(this.references?.globalCanDoStatementEntries)
    );
    if (this.retrievedWord) {
      this.wordService.updateWord(word, this.id).subscribe(w => {
        this.notificationService.openSnackBar('Wort \'' + w.name + '\' bearbeitet');
        this.dialogRef.close(w);
      }, error => {
        this.notificationService.openSnackBar(error);
      });
    } else {
      this.wordService.createWord(word).subscribe(w => {
        this.notificationService.openSnackBar('Wort \'' + w.name + '\' erstellt');
        this.dialogRef.close(w);
      }, error => {
        this.notificationService.openSnackBar(error);
      });
    }

  }

  isSingular() {
    switch (this.secondFormGroup.controls.numeralCtrl?.value) {
      case 'singular':
        return true;
      case 'plural':
        return false;
      default:
        return null;
    }
  }

  transformCountry(list: string[]): string {
    if (list.length < 2) {
      return (list.length === 0) ? null : list[0];
    } else {
      if (list.includes('Österreich') && list.includes('Deutschland') && list.includes('Schweiz')) {
        return 'DACH';
      }
      if (list.includes('Österreich') && list.includes('Deutschland')) {
        return 'A-D';
      }
      if (list.includes('Österreich') && list.includes('Schweiz')) {
        return 'A-CH';
      }
      if (list.includes('Deutschland') && list.includes('Schweiz')) {
        return 'D-CH';
      }
    }
  }

  examples(): UntypedFormArray {
    return this.fourthFormGroup.get('examples') as UntypedFormArray;
  }

  newExample(): UntypedFormGroup {
    return this._formBuilder.group({
      example: ''
    });
  }

  addExample() {
    this.examples().push(this.newExample());
  }

  removeExample(i: number) {
    this.examples().removeAt(i);
  }

  setAsCompleted() {
    if (this.stepper.selectedIndex === 0) {
      this.stepper.selected.completed = this.firstFormGroup.controls.wordCtrl?.value !== '';
    }
  }


  changeWordType(value) {
    if (value.value === 'Nomen') {
      this.secondFormGroup.controls.articlesCtrl.enable();
      this.secondFormGroup.controls.numeralCtrl.enable();
      this.secondFormGroup.controls.pluralCtrl.enable();
    } else {
      this.secondFormGroup.controls.articlesCtrl.disable();
      this.secondFormGroup.controls.numeralCtrl.disable();
      this.secondFormGroup.controls.pluralCtrl.disable();
    }
    if (value.value === 'Verb') {
      this.thirdFormGroup.controls.presenceCtrl.enable();
      this.thirdFormGroup.controls.perfectCtrl.enable();
      this.thirdFormGroup.controls.pastCtrl.enable();
    } else {
      this.thirdFormGroup.controls.presenceCtrl.disable();
      this.thirdFormGroup.controls.perfectCtrl.disable();
      this.thirdFormGroup.controls.pastCtrl.disable();
    }
  }

  openSelectWordDialogue() {
    const dialogRef = this.wordSelectDialog.open(SelectDialogComponent, {
      data: {
        selectedObjects: this.guideWordList,
        displayedColumns: ['multi-select', 'voc-ent-name', 'voc-ent-type', 'voc-ent-comment'],
        selectionEntity: 'vocab_entry',
        dialogTitle: 'Bezugswörter auswählen',
        selectionMode: 'multi-select',
        excludedId: this.id
      }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result !== undefined) {
        this.guideWordList = result;
        this.sharedService.toggleAllSelection(this.firstFormGroup.controls.guideWordCtrl, this.guideWordList);
      }
    });
  }


  openCategorySelectionDialog() {
    const dialogRef = this.wordSelectDialog.open(SelectDialogComponent, {
      data: {
        selectedObject: this.selectedCategory,
        displayedColumns: ['single-select', 'voc-cat-name', 'voc-cat-parent', 'voc-cat-type'],
        selectionEntity: 'vocab_category',
        dialogTitle: 'Überthema auswählen',
        selectionMode: 'single-select'
      }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result !== undefined) {
        this.selectedCategory = result;
        this.sharedService.toggleAllSelection(this.firstFormGroup.controls.categoryCtrl, this.selectedCategory);
      }
    });
  }


  /**
   * filter the values of the different tests with the given string
   *
   *  @param value user editable string
   */
  private _filter(value: string): string[] {
    const filterValue = value.toLowerCase();
    return this.testList.filter(option => option.toLowerCase().includes(filterValue));
  }

  protected readonly open = open;

  handleReferenceChange($event: References) {
    this.references = $event;
  }
}
